import { z } from 'zod';

/**
 * Wire schemas for check-in API responses
 * These match the backend response format (snake_case)
 *
 * IMPORTANT: Backend stores:
 * - Timestamps as ISO string DATETIME, not Unix numbers
 * - Optional fields can be null, not just undefined
 */

/**
 * Check-in status enum
 */
export const checkInStatusSchema = z.enum(['draft', 'active', 'deleted']);
export type CheckInStatus = z.infer<typeof checkInStatusSchema>;

/**
 * Check-in wire schema (matches backend response format)
 * Based on CheckInReadSchema from Gateway
 */
export const checkInWireSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  business_id: z.string(),
  menu_item_id: z.string(),
  rating_id: z.number().int().positive().nullable(),
  status: checkInStatusSchema,
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  location_accuracy_meters: z.number().nullable(),
  dining_type: z.enum(['dine_in', 'takeout']).default('dine_in'),
  created_at: z.string(), // D1 DATETIME (ISO format)
  occurred_at: z.string().nullable(),
  // Optional populated fields from JOINs
  rating_key: z.string().optional(),
  rating_label: z.string().optional(),
  business_name: z.string().optional(),
  menu_item_name: z.string().optional(),
  photos_count: z.number().int().optional(),
  has_review: z.boolean().optional(),
});

export type CheckInWire = z.infer<typeof checkInWireSchema>;

/**
 * Check-in creation data
 */
export const checkInCreateSchema = z.object({
  business_id: z.string().min(1),
  menu_item_id: z.string().min(1),
  rating_id: z.number().int().positive().optional().nullable(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  location_accuracy_meters: z.number().positive().optional().nullable(),
  occurred_at: z.string().datetime().optional().nullable(),
  dining_type: z.enum(['dine_in', 'takeout']).default('dine_in'),
});

export type CheckInCreate = z.infer<typeof checkInCreateSchema>;

/**
 * Check-in update data
 */
export const checkInUpdateSchema = z.object({
  rating_id: z.number().int().positive().optional().nullable(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  location_accuracy_meters: z.number().positive().optional().nullable(),
  occurred_at: z.string().datetime().optional().nullable(),
  dining_type: z.enum(['dine_in', 'takeout']).optional(),
});

export type CheckInUpdate = z.infer<typeof checkInUpdateSchema>;

/**
 * Paginated response wrapper schema
 * Matches createPaginatedResponseSchema from Gateway
 */
export const paginatedCheckInResponseSchema = z.object({
  items: z.array(checkInWireSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  per_page: z.number().int().min(1).max(100),
  total_pages: z.number().int().min(0),
});

export type PaginatedCheckInResponseWire = z.infer<typeof paginatedCheckInResponseSchema>;
